import { FlashNoticeVariant } from 'components/FlashNotice'
import { showFlashNotice } from 'components/FlashNotice/duck/actions'
import { callApi } from 'duck/operations'
import { all, put, takeEvery, takeLatest } from 'redux-saga/effects'
import { ActionType, getType } from 'typesafe-actions'

import {
  createPersonBegin,
  createPersonFailure,
  createPersonSuccess,
  fetchPeople,
  fetchPersonBegin,
  fetchPersonSuccess,
  updatePersonBegin,
  updatePersonFailure,
  updatePersonSuccess,
} from './actions'
import {
  apiDeleteEducationAsAdmin,
  apiDeletePositionAsAdmin,
  apiGetAllPeople,
  apiGetPersonAsCurrentUser,
  apiPostEducationAsAdmin,
  apiPostPerson,
  apiPostPositionAsAdmin,
  apiPutEducationAsAdmin,
  apiPutPersonAsAdmin,
  apiPutPositionAsAdmin,
} from './api'
import { Education, IdIndexedPeople, Person, Position } from './types'

export function* peopleSaga() {
  yield all([
    takeLatest(getType(fetchPeople.request), fetchPeopleEffect),
    takeEvery(getType(fetchPersonBegin), fetchPerson),
    takeEvery(getType(createPersonBegin), createPersonSaga),
    takeEvery(getType(createPersonSuccess), showCreatePersonSuccessFlashNotice),
    takeEvery(getType(createPersonFailure), showCreatePersonFailureFlashNotice),
    takeEvery(getType(updatePersonBegin), updatePersonSaga),
    takeEvery(getType(updatePersonSuccess), updatePersonSuccessSaga),
    takeEvery(getType(updatePersonFailure), updatePersonFailureSaga),
  ])
}

function* fetchPeopleEffect(action: ActionType<typeof fetchPeople.request>) {
  const { currentPage, limit, searchQuery } = action.payload
  const people: IdIndexedPeople = yield callApi(apiGetAllPeople, currentPage, limit, searchQuery)
  yield put(fetchPeople.success(people))
}

function* fetchPerson(action: ActionType<typeof fetchPersonBegin>) {
  const { personId } = action.payload
  const person: Person = yield callApi(apiGetPersonAsCurrentUser, personId)
  yield put(fetchPersonSuccess(person))
}

function* createPersonSaga(action: ActionType<typeof createPersonBegin>) {
  const personLocator = action.payload
  const { email, roles, educations } = personLocator

  try {
    const personData = { ...personLocator, email }
    const person: Person = yield callApi(apiPostPerson, personData)
    const updatedRoles: Position[] = yield updatePositions(roles, person.id)
    const updatedEducations: Education[] = yield updateEducations(educations, person.id)
    yield put(
      createPersonSuccess({
        ...person,
        roles: updatedRoles,
        educations: updatedEducations,
      })
    )
  } catch (error) {
    yield put(createPersonFailure(personLocator, error.message))
  }
}

function* updatePositions(positions: Position[] | undefined, personId: number): any {
  // TODO: Check how generators work
  if (!positions || !positions.length) {
    return []
  }
  const apiCalls = []
  for (let i = 0, l = positions.length; i < l; i++) {
    const position = positions[i]
    if (position.toDelete) {
      apiCalls.push(callApi(apiDeletePositionAsAdmin, position))
    } else if (position.id > 0) {
      apiCalls.push(callApi(apiPutPositionAsAdmin, position, personId))
    } else {
      apiCalls.push(callApi(apiPostPositionAsAdmin, position, personId))
    }
  }
  return yield all(apiCalls)
}

function* updateEducations(educations: Education[] | undefined, personId: number): any {
  // TODO: Check how generators work
  if (!educations || !educations.length) {
    return []
  }
  const apiCalls = []
  for (let i = 0, l = educations.length; i < l; i++) {
    const education = educations[i]
    if (education.toDelete) {
      apiCalls.push(callApi(apiDeleteEducationAsAdmin, education))
    } else if (education.id > 0) {
      apiCalls.push(callApi(apiPutEducationAsAdmin, education, personId))
    } else {
      apiCalls.push(callApi(apiPostEducationAsAdmin, education, personId))
    }
  }
  return yield all(apiCalls)
}

function* updatePersonSaga(action: ActionType<typeof updatePersonBegin>) {
  const { person } = action.payload

  const { roles, educations, email, ...personLocator } = person
  try {
    const personData = { ...personLocator, email }
    const updatedPerson: Person = yield callApi(apiPutPersonAsAdmin, personData)
    const updatedRoles: Position[] = yield updatePositions(roles, person.id)
    const updatedEducations: Education[] = yield updateEducations(educations, person.id)
    yield put(
      updatePersonSuccess({
        ...updatedPerson,
        roles: updatedRoles,
        educations: updatedEducations,
      })
    )
  } catch (error) {
    yield put(updatePersonFailure(person, error.message))
  }
}

function* updatePersonSuccessSaga(action: ActionType<typeof updatePersonSuccess>) {
  const { person } = action.payload
  yield put(fetchPersonSuccess(person))
  yield put(
    showFlashNotice({
      message: `Person "${person.firstName} ${person.lastName}" successfully updated!`,
      autoHide: true,
      variant: FlashNoticeVariant.success,
    })
  )
}

function* updatePersonFailureSaga(action: ActionType<typeof updatePersonFailure>) {
  const { person } = action.payload

  yield put(
    showFlashNotice({
      message: `Error while updating person "${person.firstName} ${person.lastName}"`,
      autoHide: false,
      variant: FlashNoticeVariant.error,
    })
  )
}

function* showCreatePersonSuccessFlashNotice(action: ActionType<typeof createPersonSuccess>) {
  const { person } = action.payload
  yield put(
    showFlashNotice({
      message: `Person "${person.firstName} ${person.lastName}" successfully created!`,
      autoHide: true,
      variant: FlashNoticeVariant.success,
    })
  )
}

function* showCreatePersonFailureFlashNotice(action: ActionType<typeof createPersonFailure>) {
  const { personData, isDuplicated } = action.payload

  if (isDuplicated) {
    yield put(
      showFlashNotice({
        message: `Person "${personData.firstName} ${personData.lastName}" already exists.`,
        autoHide: false,
        variant: FlashNoticeVariant.warning,
      })
    )
  } else {
    yield put(
      showFlashNotice({
        message: `Error while creating person "${personData.firstName} ${personData.lastName}"`,
        autoHide: false,
        variant: FlashNoticeVariant.error,
      })
    )
  }
}
